import { Download, FileText, Loader2 } from "lucide-react";
import { useState } from "react";

import { EmptyState } from "@/components/EmptyState";
import { PageContainer } from "@/components/PageContainer";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useDocumenti } from "@/hooks/useDocumenti";
import { ApiError, apiDownloadFile } from "@/lib/api";
import type { DocCategoria, Documento } from "@/types/api";

const CATEGORIA_LABEL: Record<DocCategoria, string> = {
  contratto: "Contratti e allegati",
  addendum: "Addendum",
  nda: "Riservatezza (NDA)",
  fattura: "Fatture",
  verbale: "Verbali",
  tecnico: "Documenti tecnici",
  report_sla: "Report SLA",
  materiale_formativo: "Materiale formativo",
  altro: "Altro",
};

const CATEGORIA_ORDER: DocCategoria[] = [
  "contratto",
  "addendum",
  "nda",
  "fattura",
  "verbale",
  "tecnico",
  "report_sla",
  "materiale_formativo",
  "altro",
];

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatData(iso: string): string {
  return new Date(iso).toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function DocumentiPage() {
  const { data, isLoading, isError, error } = useDocumenti();

  if (isLoading) {
    return (
      <PageContainer>
        <Header />
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer>
        <Header />
        <EmptyState
          icon={FileText}
          title="Errore nel caricamento"
          description={error instanceof Error ? error.message : "Errore sconosciuto"}
        />
      </PageContainer>
    );
  }

  const documenti = data ?? [];

  if (documenti.length === 0) {
    return (
      <PageContainer>
        <Header />
        <EmptyState
          icon={FileText}
          title="Nessun documento disponibile"
          description="I documenti caricati appariranno qui, raggruppati per categoria."
        />
      </PageContainer>
    );
  }

  const grouped = new Map<DocCategoria, Documento[]>();
  for (const d of documenti) {
    if (!grouped.has(d.categoria)) grouped.set(d.categoria, []);
    grouped.get(d.categoria)!.push(d);
  }

  return (
    <PageContainer>
      <Header />
      <div className="space-y-10">
        {CATEGORIA_ORDER.filter((c) => grouped.has(c)).map((cat) => (
          <CategoriaSection key={cat} categoria={cat} documenti={grouped.get(cat)!} />
        ))}
      </div>
    </PageContainer>
  );
}

function Header() {
  return (
    <header className="space-y-1">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">Sezione</p>
      <h1 className="text-3xl font-semibold tracking-tight text-foreground">Documenti</h1>
      <p className="text-sm text-muted-foreground max-w-2xl pt-1">
        Repository condiviso dei documenti di progetto: contratti, allegati, riservatezza,
        deliverable. Scaricabili in autonomia.
      </p>
    </header>
  );
}

function CategoriaSection({
  categoria,
  documenti,
}: {
  categoria: DocCategoria;
  documenti: Documento[];
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold text-foreground">{CATEGORIA_LABEL[categoria]}</h2>
      <div className="rounded-lg border bg-card divide-y">
        {documenti.map((d) => (
          <DocumentoRow key={d.id} documento={d} />
        ))}
      </div>
    </section>
  );
}

function DocumentoRow({ documento }: { documento: Documento }) {
  const [downloading, setDownloading] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  async function handleDownload() {
    setDownloading(true);
    setErrMsg(null);
    try {
      await apiDownloadFile(
        `/api/v1/documenti/${documento.id}/download`,
        documento.nome_file_originale
      );
    } catch (e) {
      setErrMsg(e instanceof ApiError ? e.message : "Download fallito");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="px-5 py-4 flex items-start gap-4">
      <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground truncate">{documento.nome}</div>
        {documento.descrizione && (
          <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            {documento.descrizione}
          </div>
        )}
        <div className="text-[11px] text-muted-foreground mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-mono">{documento.nome_file_originale}</span>
          <span>·</span>
          <span>{formatBytes(documento.dimensione_bytes)}</span>
          <span>·</span>
          <span>caricato il {formatData(documento.caricato_il)}</span>
          <span>·</span>
          <span className="font-mono" title={documento.hash_md5}>
            md5 {documento.hash_md5.slice(0, 8)}
          </span>
        </div>
        {errMsg && <div className="text-xs text-destructive mt-1.5">{errMsg}</div>}
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={handleDownload}
        disabled={downloading}
        className="flex-shrink-0"
      >
        {downloading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Download className="h-4 w-4" />
        )}
        <span className="ml-1.5">Scarica</span>
      </Button>
    </div>
  );
}
